import { useCallback, useMemo } from "react";
import {
  ALL_GROUP_KEY,
  Box,
  createColumnHelper,
  DataTable,
  DataTableCell,
  DataTableGroupByDropdown,
  DataTableHoverActionsCell,
  DataTableToolbar,
  DataTableToolbarLeft,
  DENSITY_CONFIG,
  renderDefaultGroupActions,
  useDataTableState,
  useWidgetSurface,
  type DataTableColumnDef,
  type DataTableGroupFieldConfig,
  type DataTableGroupSortDirection,
  type DataTableGroupType,
} from "@salestrak/ui";
import { NO_OUTCOME_KEY } from "@salestrak/widgets";

import { EntityTag } from "#app/components/entity";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { useIssueView } from "#app/components/issue-view";
import { ReadOnlyEntityCell } from "#app/components/issues/issue-columns";
import {
  ActivityFeedDocument,
  useMemberEntityResolver,
  type ActivityFeedRow,
  type ActivityWhereInput,
} from "#app/lib/data";
import { ENTITY_KIND_ICON, resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { GroupQueryProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { scopeIsWorkflowBound } from "#app/lib/filters/issue-scope";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import type { WidgetDataSlice } from "#app/widgets/dashboards/widget-drilldown";
import { useActivityGroupCounts } from "#app/widgets/definitions/shared/use-activity-group-counts";
import type { Omittable } from "@salestrak/ui";

type OutcomeActivitySlice = Extract<WidgetDataSlice, { kind: "outcomeActivity" }>;

const PAGE_LIMIT = 50;
const ORDER_BY = { creationDate: "desc" } as const;
const column = createColumnHelper<ActivityFeedRow>();
const getActivityConnection = (data: any) => data?.activities;

/**
 * The one field the dialog's table groups by. Keyed as the "Updated by" column
 * so the picker and the column name the same thing; the group band then
 * carries the member and the rows beneath it are that member's Updates.
 */
const GROUP_FIELDS: Record<string, DataTableGroupFieldConfig> = {
  author: { label: "Member", icon: ENTITY_KIND_ICON.user },
};

/** What a member group whose id the directory cannot answer for is called.
 * The group's total is exact either way, so it names the role rather than
 * asserting a person called "Unknown". */
const PENDING_MEMBER_LABEL = "Member";

/** One member group of the dialog's table: exact server total, the chip
 *  its band wears, and the predicate its own paged query adds. */
export type OutcomeActorGroup = {
  key: string;
  label: string;
  entity: EntityData;
  totalCount: number;
};

/**
 * The member group set from the `actor` aggregation over the panel's own
 * `where`: every actor with at least one Update, named through the member
 * directory and ordered by name. A member the directory has not answered for
 * yet keeps a role label and sorts on it, so the set never reorders itself as
 * names arrive one by one.
 */
export function buildActorGroups(
  counts: Record<string, number>,
  resolveMember: (userId: string) => EntityData | null,
  direction: DataTableGroupSortDirection,
): OutcomeActorGroup[] {
  const groups: OutcomeActorGroup[] = [];
  for (const [key, totalCount] of Object.entries(counts)) {
    if (totalCount <= 0) continue;
    const member = resolveMember(key);
    groups.push({
      key,
      label: member ? resolveEntityLabel(member) : PENDING_MEMBER_LABEL,
      entity: member ?? { kind: "user", id: key, user: null },
      totalCount,
    });
  }
  groups.sort((a, b) => {
    const cmp = a.label.localeCompare(b.label) || a.key.localeCompare(b.key);
    return direction === "desc" ? -cmp : cmp;
  });
  return groups;
}

function outcomeIdFromChangeset(changeset: unknown): string | null {
  if (!changeset || typeof changeset !== "object") return null;
  const age = (changeset as Record<string, unknown>).age;
  if (!age || typeof age !== "object") return null;
  const after = (age as Record<string, unknown>).after;
  if (!after || typeof after !== "object") return null;
  const reasonId = (after as Record<string, unknown>).reasonId;
  return typeof reasonId === "string" && reasonId ? reasonId : null;
}

function oneOrMany(conditions: ActivityWhereInput[]): ActivityWhereInput {
  if (conditions.length === 1) return conditions[0] ?? {};
  return { AND: conditions };
}

/**
 * Data rows the embedded table is sized for. A CONSTANT, deliberately not a
 * measurement of the clicked slice: the panel opens at ONE height for every
 * outcome and every member, so clicking through them resizes nothing and the
 * board below never reflows. A slice with fewer rows leaves quiet space; a
 * longer one scrolls inside the reserve. The half row is the peek that says
 * there is more below.
 *
 * Sizing off the data is what made the card jump — per-slice counts moved the
 * height on every click, and the largest-slice reserve that replaced them
 * still moved it whenever the window or filters changed the winner.
 */
export const EMBEDDED_RESERVE_ROWS = 6.5;

/** Translate one clicked outcome/member target into exact Activity predicates. */
export function outcomeActivitySliceWhere(
  slice?: OutcomeActivitySlice | null,
): ActivityWhereInput {
  const conditions: ActivityWhereInput[] = [
    { subject: { eq: "age" } },
    { createdBy: { exists: true } },
    // Posted Updates only, matching the actor.outcome aggregation: the Update
    // dialog stamps triggeredBy "manual", and rows that predate the stamp
    // carry none. A user-caused side-effect age write (a stage move's reset,
    // a Window edit's cycleChanged) is an event, not an Update.
    {
      OR: [
        { triggeredBy: { eq: "manual" } },
        { triggeredBy: { exists: false } },
      ],
    },
  ];

  if (slice?.actorIds?.length) {
    conditions.push({ createdBy: { in: slice.actorIds } });
  }

  if (slice?.entityTypes?.length) {
    conditions.push({ entityType: { in: slice.entityTypes } });
  }

  if (slice?.outcomeIds?.length) {
    const includesMissing = slice.outcomeIds.includes(NO_OUTCOME_KEY);
    const reasonIds = slice.outcomeIds.filter((id) => id !== NO_OUTCOME_KEY);
    const outcomeConditions: ActivityWhereInput[] = [];
    if (reasonIds.length) outcomeConditions.push({ reasonId: { in: reasonIds } });
    if (includesMissing) outcomeConditions.push({ reasonId: { exists: false } });
    if (outcomeConditions.length === 1) {
      conditions.push(outcomeConditions[0] ?? {});
    } else if (outcomeConditions.length > 1) {
      conditions.push({ OR: outcomeConditions });
    }
  }

  return oneOrMany(conditions);
}

/**
 * The Project Updates behind one outcome tile or member row, as a table that
 * wears the issue table's presentation — regular density, the Name-style
 * hover-action cell, read-only entity cells, the shared date cell — so the two
 * drilldowns on a board read as one table grammar. Embedded in the card there
 * is no toolbar: the tile or member row that opened the panel already carries
 * the count and the label. In the dialog the toolbar mirrors the issue
 * drilldown's — the group-by picker (by member) and the slice's filter chip —
 * and a grouped table pages one query per member, the group set coming from
 * the exact `actor` aggregation over the same `where`, never a loaded sample.
 */
export function OutcomePerformanceDataPanel({
  slice,
  onClearSlice,
  embedded = false,
  fillHeight = false,
}: Omittable<{
  slice?: OutcomeActivitySlice | null;
  /** Dialog only: the slice chip's clear, opening the full unfiltered data. */
  onClearSlice?: () => void;
  /** Inline in the widget card — hold the constant reserve
   * ({@link EMBEDDED_RESERVE_ROWS}) so the card opens at one height. */
  embedded?: boolean;
  /** Embedded in a container that HAS a definite height (the maximized
   * preview) — fill it instead of taking the reserve, which would strand dead
   * space under the table. */
  fillHeight?: boolean;
}>) {
  const base = useMemo(() => outcomeActivitySliceWhere(slice), [slice]);
  const { where, scope } = useScopedActivityWhere(base);
  const issueWhere =
    scope.entity && scopeIsWorkflowBound(scope.entity.type) ? scope.entityWhere : undefined;
  const panelSurface = useWidgetSurface();
  const { openIssueById } = useIssueView();
  const resolveMember = useMemberEntityResolver();
  const { states, registerState, getGroupProps } = useGroupStates<ActivityFeedRow>();
  const state = states.get(ALL_GROUP_KEY);
  // The tile or row that opened the panel already counted these rows, so its
  // count stands in for the group's total while the rows load: the column
  // header stays put and the rows' space is reserved under it, instead of the
  // header yielding to a blank on every switch (see `IssueTableQuery.total`).
  const knownTotal = slice?.total;
  // Column geometry mirrors the issue columns (Name / Stage / Assignees /
  // date) so the two drilldown tables line up on the board.
  const columns = useMemo<DataTableColumnDef<ActivityFeedRow>[]>(
    () => [
      column.accessor({
        accessorKey: "issue",
        header: "Project",
        meta: {
          width: 350,
          minWidth: 300,
          maxWidthRatio: 0.5,
          truncate: true,
          headerHoverStyle: "gradient",
        },
        // The Name cell: the whole cell is the press that opens the Project,
        // and the chip inside is inert so it does not offer a second one. A
        // row whose Project is gone has nothing to open, so it is plain text.
        cell: ({ row }) => {
          const issue = row.original.issue;
          return issue ? (
            <DataTableHoverActionsCell
              onClick={() => openIssueById(issue.issueId, { source: "widget" })}
              aria-label={`Open ${issue.name}`}
              actions={null}
            >
              <EntityTag
                entity={{ kind: "issue", id: issue.issueId, issue }}
                display="label"
                weight="regular"
                interactive={false}
              />
            </DataTableHoverActionsCell>
          ) : (
            <DataTableCell textBrightness="subtle">Project unavailable</DataTableCell>
          );
        },
      }),
      column.accessor({
        accessorKey: "reason",
        header: "Outcome",
        meta: { minWidth: 160, maxWidth: 400 },
        cell: ({ row }) => {
          const outcomeId = outcomeIdFromChangeset(row.original.changeset);
          const name = row.original.reason?.name;
          return (
            <DataTableCell textBrightness={name || outcomeId ? "high" : "subtle"}>
              {name ?? (outcomeId ? "Unavailable outcome" : "No outcome")}
            </DataTableCell>
          );
        },
      }),
      column.accessor({
        accessorKey: "author",
        header: "Updated by",
        meta: { width: 200, minWidth: 100, maxWidth: 400 },
        cell: ({ row }) => {
          const { author, createdBy } = row.original;
          return author && createdBy ? (
            // A cell never labels a member: the avatar alone, hovering to
            // the member preview that carries the name.
            <ReadOnlyEntityCell>
              <EntityTag
                entity={{ kind: "user", id: createdBy, user: author }}
                display="visual"
                ghost={false}
              />
            </ReadOnlyEntityCell>
          ) : (
            <DataTableCell textBrightness="subtle">Unknown</DataTableCell>
          );
        },
      }),
      column.date({
        accessorKey: "creationDate",
        header: "Updated",
        meta: { width: 120, minWidth: 100, maxWidth: 400 },
      }),
    ],
    [openIssueById],
  );
  // Grouping is a per-viewer view choice on the dialog — plain component
  // state, never a config key. The dialog opens grouped by member: the
  // question this table answers is who posted what, so the bands are the
  // reading and the flat list is the opt-out. The embedded panel has no
  // toolbar and ignores the field, so it never groups.
  const { groupByField, setGroupByField, isGrouped, groupByLabels, groupByIcons, groupSortDirection, setGroupSortDirection } =
    useDataTableState({ columns, groupFields: GROUP_FIELDS, defaultGroupByField: "author" });
  const grouped = !embedded && isGrouped;
  const actorCounts = useActivityGroupCounts({ groupBy: "actor", where, skip: !grouped });
  const actorGroups = useMemo(
    () => (grouped && actorCounts.ready ? buildActorGroups(actorCounts.counts, resolveMember, groupSortDirection) : []),
    [grouped, actorCounts.ready, actorCounts.counts, resolveMember, groupSortDirection],
  );
  // A grouped set that answered empty falls back to the ungrouped sentinel, so
  // the table shows its rows-empty state instead of a headerless void.
  const memberGroups = grouped && actorGroups.length > 0 ? actorGroups : null;
  const entityByKey = useMemo(() => {
    const map = new Map<string, EntityData>();
    for (const group of actorGroups) map.set(group.key, group.entity);
    return map;
  }, [actorGroups]);
  const groups = useMemo<DataTableGroupType<ActivityFeedRow>[]>(() => {
    if (memberGroups) {
      return memberGroups.map((group) => ({
        key: group.key,
        label: group.label,
        totalCount: states.get(group.key)?.total ?? group.totalCount,
        ...getGroupProps(group.key),
      }));
    }
    return [
      {
        // The ungrouped sentinel: a lone group under this key draws no group
        // header.
        key: ALL_GROUP_KEY,
        label: "Project Updates",
        totalCount: state && !state.loading ? state.total : knownTotal,
        ...getGroupProps(ALL_GROUP_KEY),
      },
    ];
  }, [memberGroups, getGroupProps, states, state, knownTotal]);
  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      const entity = entityByKey.get(groupKey);
      if (!entity) return groupKey;
      return (
        <Box direction="row" align="center" gap="sm" overflow="hidden">
          <EntityTag entity={entity} interactive={false} />
        </Box>
      );
    },
    [entityByKey],
  );
  const tableLoading = grouped ? !actorCounts.ready : (state?.loading ?? true);
  const density = DENSITY_CONFIG.regular;
  const reserved = embedded && !fillHeight;
  // One band sits above the rows — the column header. The ungrouped table
  // draws no group header and the panel has no toolbar.
  const embeddedHeight = density.headerHeight + EMBEDDED_RESERVE_ROWS * density.rowHeight;

  return (
    <Box
      direction="column"
      // The reserve is a FLOOR. `grow` leaves the flex basis at auto, so the
      // `height` property is where the panel starts, and the free space in the
      // island — the row remainder an autoHeight card hands its body —
      // stretches the table instead of surfacing the island's fill under it.
      // `fill="flex"` cannot do this: `flex-1` zeroes the basis, and inside a
      // column that zero basis is the main size, so the explicit reserve
      // silently loses and the panel sizes to its rows. Everywhere else the
      // parent has a definite height to fill, which is what `fill="flex"` is
      // for.
      fill={reserved ? "width" : "flex"}
      grow={reserved ? true : undefined}
      shrink={reserved ? false : undefined}
      overflow="hidden"
      height={reserved ? embeddedHeight : undefined}
    >
      {memberGroups ? (
        memberGroups.map((group) => (
          <GroupQueryProvider<ActivityFeedRow>
            key={group.key}
            groupKey={group.key}
            document={ActivityFeedDocument}
            variables={{
              where: { AND: [where, { createdBy: { eq: group.key } }] },
              issueWhere,
              orderBy: ORDER_BY,
              limit: PAGE_LIMIT,
            }}
            getConnection={getActivityConnection}
            onStateChange={registerState}
          />
        ))
      ) : (
        <GroupQueryProvider<ActivityFeedRow>
          groupKey={ALL_GROUP_KEY}
          document={ActivityFeedDocument}
          variables={{ where, issueWhere, orderBy: ORDER_BY, limit: PAGE_LIMIT }}
          getConnection={getActivityConnection}
          onStateChange={registerState}
        />
      )}
      {embedded ? null : (
        <DataTableToolbar>
          <DataTableToolbarLeft>
            <DataTableGroupByDropdown
              groupByField={groupByField}
              setGroupByField={setGroupByField}
              groupByLabels={groupByLabels}
              groupByIcons={groupByIcons}
              groupSortDirection={groupSortDirection}
              setGroupSortDirection={setGroupSortDirection}
              iconOnly
            />
            {slice ? (
              <FilterToggle
                size="xs"
                icon="filter"
                label={slice.label}
                selected
                onClear={onClearSlice}
                tooltip={`Filtering by: ${slice.label}`}
                clearTooltip="Clear filter"
              />
            ) : null}
          </DataTableToolbarLeft>
        </DataTableToolbar>
      )}
      <DataTable
        // Inside a widget card, so the opaque parts — sticky header, pinned
        // cells, row washes — paint whatever the card painted, not the page.
        surface={panelSurface}
        columns={columns}
        groups={groups}
        getRowId={(row) => row.activityId}
        loading={tableLoading}
        stickyHeader
        // Under the toolbar the table's own gutter carries the spacing, as
        // scroll content rows pass through.
        gutterTop={embedded ? undefined : "md"}
        density="regular"
        fillColumnId="issue"
        groupTitle={memberGroups ? renderGroupTitle : undefined}
        groupActions={memberGroups ? renderDefaultGroupActions : undefined}
        // The island is full-bleed, so restore the content inset here: row
        // backgrounds and dividers reach the border while cell text stays
        // padded.
        paddingX="md"
        paddingXAlign="content"
      />
    </Box>
  );
}
